import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Gavel, Timer } from 'lucide-react';
import { TASK_PRIORITIES, TASK_PRIORITY_META, formatDuration, formatTaskReference } from '@ssn/shared';
import { useReferenceData, useReviewQueue } from '@/lib/queries';
import { useSession } from '@/session/SessionProvider';
import { PageHeader, Panel, StatTile } from '@/components/ui/Panel';
import { Select, TextInput } from '@/components/ui/Form';
import { Button } from '@/components/ui/Button';
import { EmptyState, ErrorState, SkeletonRows } from '@/components/ui/Feedback';
import { Pagination } from '@/components/ui/Pagination';
import { PriorityBadge } from '@/components/domain/status';
import { UserChip } from '@/components/ui/Avatar';
import { DeadlineCell } from '@/components/domain/TaskTable';
import { TableWrap, Td, Th, Tr } from '@/components/ui/Table';
import { formatDateTime } from '@/lib/format';

const SORTS = [
  { value: 'PRIORITY', label: 'Priority, then longest waiting' },
  { value: 'WAITING_LONGEST', label: 'Waiting longest' },
  { value: 'WAITING_SHORTEST', label: 'Waiting shortest' },
  { value: 'DEADLINE', label: 'Deadline' },
  { value: 'DEVELOPER', label: 'Developer' },
] as const;

/**
 * The Director Review Queue (§8).
 *
 * Sorted by priority and then by how long each task has been waiting, because
 * that combination is what a director actually triages by. Waiting time is the
 * loudest number on each row for the same reason.
 */
export function ReviewQueuePage() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const reference = useReferenceData();

  const query = useMemo(() => {
    const built: Record<string, unknown> = {
      sort: params.get('sort') ?? 'PRIORITY',
      page: Number(params.get('page') ?? '1'),
      pageSize: 25,
    };
    for (const key of ['q', 'priority', 'assigneeId', 'projectId']) {
      const value = params.get(key);
      if (value) built[key] = value;
    }
    return built;
  }, [params]);

  const { data, isPending, error, refetch } = useReviewQueue(query);

  const update = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (!value) next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setParams(next, { replace: true });
  };

  const canDecide = can('review:approve');

  return (
    <>
      <PageHeader
        title="Final Review"
        description={
          canDecide
            ? 'Tasks the development team has submitted for director sign-off.'
            : 'Tasks awaiting director sign-off. Only Development Directors can approve or request changes.'
        }
      />

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <StatTile
          label="Awaiting review"
          value={data?.totalWaiting ?? '—'}
          icon={<Gavel aria-hidden className="size-4" />}
          tone={(data?.totalWaiting ?? 0) > 0 ? 'warning' : 'default'}
        />
        <StatTile
          label="Longest wait"
          value={data?.oldestWaitingMs != null ? formatDuration(data.oldestWaitingMs) : '—'}
          icon={<Timer aria-hidden className="size-4" />}
          tone={(data?.oldestWaitingMs ?? 0) > 24 * 3_600_000 ? 'critical' : 'default'}
          hint={(data?.oldestWaitingMs ?? 0) > 24 * 3_600_000 ? 'Somebody has been waiting over a day.' : undefined}
        />
        <StatTile label="On this page" value={data?.items.length ?? '—'} />
      </div>

      <Panel flush>
        <div className="flex flex-wrap items-end gap-3 border-b border-border p-3">
          <TextInput
            label="Search"
            value={params.get('q') ?? ''}
            onChange={(event) => update('q', event.target.value)}
            placeholder="Title or task number…"
            containerClassName="min-w-[14rem] flex-1"
          />
          <Select
            label="Priority"
            value={params.get('priority') ?? ''}
            onChange={(event) => update('priority', event.target.value)}
            placeholder="Any priority"
            options={TASK_PRIORITIES.map((priority) => ({
              value: priority,
              label: TASK_PRIORITY_META[priority].label,
            }))}
          />
          <Select
            label="Developer"
            value={params.get('assigneeId') ?? ''}
            onChange={(event) => update('assigneeId', event.target.value)}
            placeholder="Anyone"
            options={(reference.data?.assignableUsers ?? []).map((user) => ({
              value: user.id,
              label: user.displayName,
            }))}
          />
          <Select
            label="Sort"
            value={String(query['sort'])}
            onChange={(event) => update('sort', event.target.value)}
            options={SORTS.map((sort) => ({ value: sort.value, label: sort.label }))}
          />
          {[...params.keys()].some((key) => key !== 'sort') && (
            <Button variant="ghost" onClick={() => setParams(new URLSearchParams(), { replace: true })}>
              Clear
            </Button>
          )}
        </div>

        {error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : isPending ? (
          <SkeletonRows rows={5} className="p-4" />
        ) : data && data.items.length === 0 ? (
          <EmptyState
            icon={<Gavel aria-hidden className="size-5" />}
            title="Nothing is waiting for review"
            description="Every submitted task has been signed off. The team is not blocked on you."
          />
        ) : (
          <>
            <TableWrap>
              <thead>
                <tr>
                  <Th className="w-28">Priority</Th>
                  <Th>Task</Th>
                  <Th className="w-44">Developer</Th>
                  <Th className="w-32" align="right">Waiting</Th>
                  <Th className="w-28" align="right">Rounds</Th>
                  <Th className="w-32">Deadline</Th>
                  <Th className="w-24" align="right" />
                </tr>
              </thead>
              <tbody>
                {data?.items.map((task) => {
                  const waitedOverADay = task.waitingMs > 24 * 3_600_000;
                  return (
                    <Tr key={task.id}>
                      <Td>
                        <PriorityBadge priority={task.priority} size="sm" />
                      </Td>
                      <Td>
                        <Link to={`/review/${task.reference}`} className="group block min-w-0">
                          <span className="block truncate font-medium text-text group-hover:text-accent-text">
                            {task.title}
                          </span>
                          <span className="mt-0.5 block font-mono text-[11px] text-text-subtle">
                            {formatTaskReference(task.reference)}
                            {task.lastReviewComment && ' · previously returned'}
                          </span>
                        </Link>
                      </Td>
                      <Td>
                        <UserChip user={task.assignee} size="xs" />
                      </Td>
                      <Td align="right">
                        <span
                          title={`Submitted ${formatDateTime(task.submittedForReviewAt)}`}
                          className={`text-sm tabular ${waitedOverADay ? 'font-medium text-critical-tone' : 'text-text'}`}
                        >
                          {formatDuration(task.waitingMs)}
                        </span>
                      </Td>
                      <Td align="right" className="tabular text-text-muted">
                        {task.previousReviewCycles > 0 ? task.previousReviewCycles : '—'}
                      </Td>
                      <Td>
                        <DeadlineCell task={task} />
                      </Td>
                      <Td align="right">
                        <Link
                          to={`/review/${task.reference}`}
                          className="text-xs font-medium text-accent-text hover:underline"
                        >
                          Review
                        </Link>
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </TableWrap>

            {data && (
              <Pagination
                page={data.page}
                pageSize={data.pageSize}
                total={data.total}
                totalPages={data.totalPages}
                noun="tasks"
                onPageChange={(page) => update('page', String(page))}
              />
            )}
          </>
        )}
      </Panel>
    </>
  );
}
